import { useCallback, useContext, useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CalendarIcon } from "../assets/images/icons/ic-calendar.svg";
import { ReactComponent as EmailIcon } from "../assets/images/icons/ic-email.svg";
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-image-edit.svg";
import { ReactComponent as LogoutIcon } from "../assets/images/icons/ic-logout.svg";
import { ReactComponent as CartIcon } from "../assets/images/icons/ic-cart.svg"
import { ReactComponent as CopIcon } from "../assets/images/icons/ic-cop.svg"
import { ReactComponent as BagIcon } from "../assets/images/icons/ic-bag.svg"
import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png";
import MainContext from "../common/MainContext";
import Button from "../components/Button";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import Skeleton from "../components/Skeleton";
import { auth } from "../firebase";
import styles from "./Profile.module.css";
import EditProfile from "../components/EditProfile";
import CalendlyIntegration from "../components/CalendlyIntegration";
import { AdminRole } from "../common/constants";

const Profile = ({ }) => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const context = useContext(MainContext)
  const [showEditPage, setShowEditPage] = useState(false)
  const [activeTab, setActiveTab] = useState(() => (
    new URLSearchParams(window.location.search).has('calendly') ? 'integrations' : 'profile'
  ))

  const companyAssociation = context.user?.company_id ?? context.user?.company?.id ?? context.user?.company
  const isCompanyAdmin = Boolean(companyAssociation)
  const companyData = context.user?.company && typeof context.user.company === 'object' ? context.user.company : null
  const canUseIntegrations = context.user?.role === AdminRole.Sales || context.user?.role === AdminRole.Main

  useEffect(() => {
    window.scrollTo({
      top: 0,
    })
  }, [])

  useEffect(() => {
    if (context.user && !canUseIntegrations && activeTab === 'integrations') {
      setActiveTab('profile')
    }
  }, [activeTab, canUseIntegrations, context.user])

  const logout = useCallback(async () => {
    await auth.signOut()
    context.setUser(null)
    navigate("/")
  }, [])

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>
            Profilo
          </title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          {!showEditPage && <header className={styles.pageHeader}>
            <div>
              <h1>{activeTab === 'integrations' ? 'App e Integrazioni' : 'Il mio profilo'}</h1>
              <p>
                {activeTab === 'integrations'
                  ? 'Collega e configura le applicazioni utilizzate nel tuo flusso di lavoro.'
                  : 'Le informazioni del tuo account e della tua organizzazione.'}
              </p>
            </div>
            {activeTab === 'profile' && (
              <Button disabled={!context.user} onClick={() => setShowEditPage(true)} accentColor="var(--primary)" style={{borderRadius: 8, padding: '.65rem 1rem', fontSize: '.75rem'}}><EditIcon /> Modifica profilo</Button>
            )}
          </header>}
          {
            showEditPage && <EditProfile onBack={() => setShowEditPage(false)} />
          }

          {
            !showEditPage && canUseIntegrations &&
            <nav className={styles.tabs} role="tablist" aria-label="Sezioni del profilo">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'profile'}
                onClick={() => setActiveTab('profile')}
              >
                Profilo
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'integrations'}
                onClick={() => setActiveTab('integrations')}
              >
                App e Integrazioni
              </button>
            </nav>
          }

          {
            !showEditPage && activeTab === 'profile' &&
            <div className={`${styles.sectionInner} ${isCompanyAdmin ? styles.withCompany : styles.singleColumn}`}>
              <div className={styles.profileContainer}>
                <div className={styles.profileCard}>
                  <div className={styles.admin}>
                    {
                      context.user?.picture &&
                      <img className={styles.profilePic} src={context.user?.picture} alt='' />
                    }
                    {
                      !context.user?.picture &&
                      <img className={styles.profilePic} src={UserPlaceholder} alt='' />
                    }
                    {
                      !context.user &&
                      <Skeleton type="rect" width="164px" height="24px" borderRadius={"12px"} />
                    }
                    {
                      context.user &&
                      <div className={styles.username}>{`${context.user?.name} ${context.user?.surname}`}</div>
                    }

                  </div>
                  <div className={styles.profileInfo}><h2 className={styles.cardTitle}>Informazioni account</h2>
                    <div className={styles.profileRow}>
                      <div className={styles.profileLabel}>
                        <EmailIcon />
                        <span>{t("profile.email")}</span>
                      </div>
                      <div className={styles.profileRowValue}>{context.user?.email || '-'}</div>
                    </div>
                    <div className={styles.profileRow}>
                      <div className={styles.profileLabel}>
                        <CalendarIcon />
                        <span>Data creazione</span>
                      </div>
                      <div className={styles.profileRowValue}>{context.user?.created_at ? new Date(context.user.created_at).toLocaleString("it-IT") : '-'}</div>
                    </div>
                    {!isCompanyAdmin &&
                      <div className={styles.profileRow}>
                        <div className={styles.profileLabel}>
                          <BagIcon />
                          <span>Ruolo</span>
                        </div>
                        {
                          context.user?.role === AdminRole.Main &&
                          <div className={styles.profileRowValue}>Main <CopIcon /></div>
                        }
                        {
                          context.user?.role === AdminRole.Sales &&
                          <div className={styles.profileRowValue}>Sales <CartIcon /></div>
                        }
                      </div>
                    }
                  </div>
                  <button
                    type="button"
                    className={styles.logout}
                    onClick={logout}
                  >
                    <LogoutIcon />
                    Esci dall’account
                  </button>
                </div>
              </div>

              {isCompanyAdmin &&
                <div className={styles.companyContainer}>
                  <div className={styles.companyCard}>
                    <div className={styles.companyHeader}>
                      {companyData?.logo &&
                        <img className={styles.companyLogo} src={companyData.logo} alt='Logo azienda' />
                      }
                      <div className={styles.companyTitleWrap}>
                        <div className={styles.companyCaption}>Azienda associata</div>
                        <div className={styles.companyName}>{companyData?.name || '-'}</div>
                      </div>
                    </div>
                    <div className={styles.companyInfo}>
                      <div className={styles.companyRow}><span>Email</span><strong>{companyData?.email || '-'}</strong></div>
                      <div className={styles.companyRow}><span>Telefono</span><strong>{companyData?.phone || '-'}</strong></div>
                      <div className={styles.companyRow}><span>Sito</span><strong>{companyData?.website || '-'}</strong></div>
                      <div className={styles.companyRow}><span>Partita IVA</span><strong>{companyData?.vat_number || '-'}</strong></div>
                    </div>
                  </div>
                </div>
              }

            </div>
          }

          {
            !showEditPage && canUseIntegrations && activeTab === 'integrations' &&
            <div className={styles.integrationsPanel} role="tabpanel">
              <CalendlyIntegration />
            </div>
          }
        </div>
      </div>

    </HeaderFooterLayout>

  )
}


export default Profile
